export const PICKER_SCRIPT_URL = 'https://apis.google.com/js/api.js';

interface PickerResponse {
  action: string;
  docs?: Array<{ id: string }>;
}
interface PickerBuilder {
  setDeveloperKey(k: string): PickerBuilder;
  setAppId(a: string): PickerBuilder;
  setOAuthToken(t: string): PickerBuilder;
  addView(v: unknown): PickerBuilder;
  setCallback(cb: (r: PickerResponse) => void): PickerBuilder;
  build(): { setVisible(v: boolean): void };
}
interface PickerWindow {
  gapi: { load(name: string, cb: () => void): void };
  google: { picker: { PickerBuilder: new () => PickerBuilder; ViewId: { SPREADSHEETS: unknown } } };
}

const loads = new WeakMap<Document, { script: HTMLScriptElement; ready: Promise<void> }>();

function loadScript(doc: Document): Promise<void> {
  const cached = loads.get(doc);
  if (cached && cached.script.isConnected) return cached.ready;
  const script = doc.createElement('script');
  const ready = new Promise<void>((resolve, reject) => {
    script.src = PICKER_SCRIPT_URL;
    script.async = true;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => {
      script.remove();
      loads.delete(doc);
      reject(new Error('Could not load Google Picker. Check your connection and try again.'));
    });
    doc.head.appendChild(script);
  });
  loads.set(doc, { script, ready });
  return ready;
}

/** Resolves the picked spreadsheet's file id, or null when the user closes the picker. */
export async function pickSpreadsheet(
  opts: { apiKey: string; appId: string; accessToken: string },
  doc: Document = document
): Promise<string | null> {
  await loadScript(doc);
  const win = doc.defaultView as unknown as PickerWindow;
  await new Promise<void>((resolve) => win.gapi.load('picker', resolve));
  const { PickerBuilder, ViewId } = win.google.picker;
  return new Promise<string | null>((resolve) => {
    new PickerBuilder()
      .setDeveloperKey(opts.apiKey)
      .setAppId(opts.appId)
      .setOAuthToken(opts.accessToken)
      .addView(ViewId.SPREADSHEETS)
      .setCallback((r) => {
        if (r.action === 'picked') resolve(r.docs?.[0]?.id ?? null);
        else if (r.action === 'cancel') resolve(null);
      })
      .build()
      .setVisible(true);
  });
}
